import React from "react";
import axios from "axios";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { installErrorReporting } from "@/components/ErrorBoundary";
import { isMinibioHost } from "@/lib/minibio";

installErrorReporting();

// Zweit-Domain minibio.info: absolute Backend-URLs (dimedial.studio) in fetch/XHR auf RELATIV
// umschreiben → der Cloudflare-Worker reicht /api 1:1 an dimedial.studio weiter (same-origin, kein
// CORS/Preflight). Gilt nur auf minibio.info; auf dimedial.studio unverändert. Bilder/OG bleiben absolut.
const _BE = process.env.REACT_APP_BACKEND_URL;
if (_BE && isMinibioHost()) {
  const toRel = (u) => (typeof u === "string" && u.startsWith(_BE + "/")) ? u.slice(_BE.length) : u;
  const _xopen = window.XMLHttpRequest.prototype.open;
  window.XMLHttpRequest.prototype.open = function (m, url, ...rest) { return _xopen.call(this, m, toRel(url), ...rest); };
  const _nativeFetch = window.fetch.bind(window);
  window.fetch = (input, init) => {
    if (typeof input === "string") return _nativeFetch(toRel(input), init);
    if (input && typeof input.url === "string" && input.url.startsWith(_BE + "/")) return _nativeFetch(new Request(toRel(input.url), input), init);
    return _nativeFetch(input, init);
  };
}

// CSRF-Schutz für Cookie-Sessions: Custom-Header erzwingt CORS-Preflight; der Server verlangt ihn bei zustandsändernden Endpunkten.
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
// Gleiches für alle fetch()-Aufrufe an unser Backend (35 direkte fetch-POSTs im Code)
const _fetch = window.fetch.bind(window);
window.fetch = (input, init = {}) => {
  const url = typeof input === "string" ? input : input?.url || "";
  const ours = url.startsWith("/api/") || url.startsWith(`${process.env.REACT_APP_BACKEND_URL}/api/`);
  if (!ours) return _fetch(input, init);
  const headers = new window.Headers(init.headers || (typeof input !== "string" && input?.headers) || undefined);
  if (!headers.has("X-Requested-With")) headers.set("X-Requested-With", "XMLHttpRequest");
  return _fetch(input, { ...init, headers });
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const rootElement = document.getElementById("root");
const app = (
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>
);

// FastAPI/Pydantic-Validierungsfehler (detail = Liste von Objekten) immer als lesbaren Text liefern – sonst React #31 in Toasts/Meldungen.
axios.interceptors.response.use(undefined, (error) => {
  const d = error?.response?.data;
  if (d && d.detail != null && typeof d.detail !== "string" && !(d.detail && !Array.isArray(d.detail) && d.detail.code)) {  // strukturierte Fehler mit `code` (z. B. lexoffice 409) bleiben Objekte
    const list = Array.isArray(d.detail) ? d.detail : [d.detail];
    d.detail = list.map((x) => (typeof x === "string" ? x : `${(x.loc || []).filter((l) => l !== "body").join(".")}: ${x.msg || JSON.stringify(x)}`)).join(" · ") || "Ungültige Eingabe";
  }
  return Promise.reject(error);
});

// Vorgerendertes HTML (Bots/SEO) hydrieren; kleine Abweichungen (#418/#423/#425) repariert React selbst – nicht als Fehler melden.
const onRecoverableError = (err) => { if (!/#41[89]|#42[35]|hydrat/i.test(String(err?.message || err))) console.error(err); };
// Der SPA-Fallback liefert für JEDE Route die vorgerenderte Startseite (build/index.html). Hydrieren darf nur die Route, die wirklich vorgerendert wurde –
// sonst stünde auf /kunden, /portal, /app kurz (oder bei Hydrationsfehler dauerhaft) die Marketing-Startseite. Alles andere: Root leeren und frisch rendern.
const aktuellerPfad = window.location.pathname.replace(/\/+$/, "") || "/";
const vorgerendert = rootElement.dataset.prerender;
if (rootElement.hasChildNodes() && vorgerendert === aktuellerPfad) {
  ReactDOM.hydrateRoot(rootElement, app, { onRecoverableError });
} else {
  if (rootElement.hasChildNodes()) rootElement.replaceChildren();
  ReactDOM.createRoot(rootElement).render(app);
  document.documentElement.classList.remove("spa-fallback");  // Head-Guard der vorgerenderten Seite wieder aufheben
}
